import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSubscription } from '../context/SubscriptionContext';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiHelper';

import { formatPrice } from '../utils/formatHelper';
import placeholderImage from '../assets/images/placeholder.jpg';
import LoadingSpinner from '../components/LoadingSpinner';
import { showSuccess, showError, showWarning, showInfo, showServerError } from '../utils/toastHelper';

function UserSubscription() {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    setSubscriptionPlan,
    addToPackage,
    items,
    resetPackage,
    setIsEditing,
    setSubscriptionType  // Bunu ekleyelim
  } = useSubscription();
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [notFoundError, setNotFoundError] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(true);
  const [pastSubscriptions, setPastSubscriptions] = useState([]);

  // Address edit modal states
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [addressFormData, setAddressFormData] = useState({
    shippingAddress: {
      fullName: '',
      address: '',
      city: '',
      district: '',
      country: 'Turkey',
      phone: '',
      zipCode: ''
    },
    billingAddress: {
      fullName: '',
      address: '',
      city: '',
      district: '',
      country: 'Turkey',
      phone: '',
      zipCode: '',
      type: 'individual'
    }
  });
  const [sameAsBilling, setSameAsBilling] = useState(false);

  // Cancel subscription modal states
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelOrders, setCancelOrders] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  // Edit products confirmation modal states
  // Edit products confirmation modal states
  const [showEditConfirmModal, setShowEditConfirmModal] = useState(false);

  // Payment/Card Update Modal states
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentFormContent, setPaymentFormContent] = useState(null);

  // Safe setState function to prevent memory leaks
  const safeSetState = (callback) => {
    if (isComponentMounted) {
      callback();
    }
  };

  const fetchSubscription = async () => {
    try {
      safeSetState(() => setLoading(true));
      const response = await apiRequest(`/subscriptions/user/${id}`);

      if (response.success) {
        safeSetState(() => {
          setSubscription(response.subscription);
          setPastSubscriptions(response.pastSubscriptions || []);
          console.log('Subscription response:', response);

          // Initialize address form data - support both direct and customerInfo structures
          const subscription = response.subscription;
          const customerInfo = subscription.customerInfo;
          const shippingAddr = subscription.shippingAddress || customerInfo?.shippingAddress;
          const billingAddr = subscription.billingAddress || customerInfo?.billingAddress;

          if (shippingAddr || billingAddr || customerInfo) {
            const fullName = customerInfo ? `${customerInfo.name || ''} ${customerInfo.surname || ''}`.trim() : '';

            setAddressFormData({
              shippingAddress: {
                fullName: shippingAddr?.fullName || shippingAddr?.contactName || fullName,
                address: shippingAddr?.address || customerInfo?.registrationAddress || '',
                city: shippingAddr?.city || customerInfo?.city || '',
                district: shippingAddr?.district || customerInfo?.district || '',
                country: shippingAddr?.country || customerInfo?.country || 'Turkey',
                phone: shippingAddr?.phone || shippingAddr.gsmNumber || customerInfo?.gsmNumber || '',
                zipCode: shippingAddr?.zipCode || customerInfo?.zipCode || ''
              },
              billingAddress: {
                fullName: billingAddr?.fullName || billingAddr?.contactName || fullName,
                address: billingAddr?.address || customerInfo?.registrationAddress || '',
                city: billingAddr?.city || customerInfo?.city || '',
                district: billingAddr?.district || customerInfo?.district || '',
                country: billingAddr?.country || customerInfo?.country || 'Turkey',
                phone: billingAddr?.phone || billingAddr?.gsmNumber || customerInfo?.gsmNumber || '',
                zipCode: billingAddr?.zipCode || customerInfo?.zipCode || '',
                type: billingAddr?.type || customerInfo?.type || 'individual'
              }
            });

            // Check if billing address is same as shipping address
            if (shippingAddr && billingAddr) {
              const shippingName = shippingAddr.fullName || shippingAddr.contactName;
              const billingName = billingAddr.fullName || billingAddr.contactName;
              const shippingPhone = shippingAddr.phone || shippingAddr.gsmNumber;
              const billingPhone = billingAddr.phone || billingAddr.gsmNumber;

              const isSame = shippingName === billingName &&
                shippingAddr.address === billingAddr.address &&
                shippingAddr.city === billingAddr.city &&
                shippingAddr.district === billingAddr.district &&
                shippingPhone === billingPhone;
              setSameAsBilling(isSame);
            }
          }

          // Abonelik planını state'e kaydet (ödeme için gerekecek)
          if (response.subscription.status === 'PENDING') {
            // Payment.js'deki yapıyla uyumlu olacak şekilde set et
            setSubscriptionPlan(response.subscription);
          }
        });
      } else {
        if (response.notFound) {
          safeSetState(() => setNotFoundError('not_found'));
        } else {
          if (isComponentMounted) {
            showError(response.message || 'Abonelik bilgileri alınırken bir hata oluştu');
          }
        }
      }
    } catch (error) {
      console.error('Failed to fetch subscription:', error);
      if (isComponentMounted) {
        // 401 hatası için ek toast gösterme (zaten showSessionTimeout çağrıldı)
        if (error.isAuthError) {
          return;
        }
        if (error.apiError) {
          showServerError();
        } else if (error.response?.status === 404 || error.data?.notFound) {
          safeSetState(() => setNotFoundError('not_found'));
        } else {
          showError(error.message || 'Abonelik bilgileri alınırken bir hata oluştu');
        }
      }
    } finally {
      safeSetState(() => setLoading(false));
    }
  };

  useEffect(() => {
    setIsComponentMounted(true);
    let isMounted = true;

    const fetchData = async () => {
      try {
        if (isMounted) {
          await fetchSubscription();

          // Add payment modal styles
          const style = document.createElement('style');
          style.textContent = `
            .payment-modal {
                position: fixed;
                top: 0;
                left: 0;
                right: 0;
                bottom: 0;
                background-color: rgba(0, 0, 0, 0.5);
                display: flex;
                align-items: center;
                justify-content: center;
                z-index: 9999;
            }
            .payment-modal-content {
                background: white;
                padding: 20px;
                border-radius: 8px;
                position: relative;
                width: 100%;
                max-width: 500px;
                max-height: 90vh;
                overflow-y: auto;
            }
            .payment-modal-close {
                position: absolute;
                top: 10px;
                right: 10px;
                background: none;
                border: none;
                font-size: 24px;
                cursor: pointer;
                color: #666;
                z-index: 1;
            }
            #iyzipay-checkout-form {
                min-height: 400px;
            }
          `;
          document.head.appendChild(style);
          return () => {
            if (document.head.contains(style)) {
              document.head.removeChild(style);
            }
          };
        }
      } catch (error) {
        console.error('Error in useEffect:', error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      setIsComponentMounted(false);
    };
  }, [id]);

  const handlePaymentClick = () => {
    if (!isComponentMounted) return;

    // Payment.js'deki yapıyla uyumlu olacak şekilde set et
    // Payment.js fetchSubscriptionDetails'te response.subscription'ı direkt set ediyor
    // Bu yüzden aynı yapıyı kullanmalıyız
    setSubscriptionPlan(subscription);
    navigate('/subscription/payment');
  };



  const handleCancelSubscription = () => {
    setShowCancelModal(true);
  };

  const handleConfirmCancel = async () => {
    if (!cancelReason.trim()) {
      showError('Lütfen iptal sebebini belirtiniz');
      return;
    }

    try {
      setActionLoading(true);
      const response = await apiRequest(`/subscriptions/${id}/cancel-request`, {
        method: 'POST',
        body: JSON.stringify({
          cancelReason: cancelReason.trim(),
          cancelOrders: cancelOrders
        })
      });

      if (response.success) {
        // Ana başarı mesajı
        showSuccess(response.message);

        // Eğer warning mesajı varsa
        if (response.warning) {
          showWarning(response.warning);
        }

        // Email durumu bildirimi
        if (response.emailSent) {
          showInfo('İptal bilgilendirme emaili gönderildi.');
        }

        fetchSubscription();

        // Modal'ı kapat ve form'u temizle
        setShowCancelModal(false);
        setCancelReason('');
        setCancelOrders(false);
      } else {
        showError(response.message || 'Abonelik iptal edilirken bir hata oluştu');
      }
    } catch (error) {
      // Modal'ı kapat ve form'u temizle
      setShowCancelModal(false);
      setCancelReason('');
      setCancelOrders(false);
      console.error('Failed to cancel subscription:', error);
      showError(error.message || 'Abonelik iptal edilirken bir hata oluştu');
    } finally {
      setActionLoading(false);
    }
  };

  const handleEditProducts = () => {
    // Aktif abonelik için onay modal'ı göster
    if (subscription.status === 'ACTIVE') {
      setShowEditConfirmModal(true);
    } else {
      // Diğer durumlar için direkt işleme devam et
      proceedWithEditProducts();
    }
  };

  const handleUpdateCard = async () => {
    try {
      setActionLoading(true);
      const response = await apiRequest(`/subscriptions/${subscription.referenceCode}/card-update`, {
        method: 'POST'
      });

      if (response.success && response.checkoutFormContent) {
        setPaymentFormContent(response.checkoutFormContent);
        setShowPaymentModal(true);
        initializePaymentForm(response.checkoutFormContent);
      } else {
        showError(response.message || 'Kart güncelleme formu oluşturulamadı');
      }
    } catch (error) {
      console.error('Card update error:', error);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Kart güncelleme başlatılamadı');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleRetryPayment = async () => {
    try {
      setActionLoading(true);
      const response = await apiRequest(`/subscriptions/${subscription.referenceCode}/retry-payment`, {
        method: 'POST'
      });

      if (response.success) {
        showSuccess('Ödeme tekrarı başarıyla tetiklendi. Lütfen bekleyiniz.');
        // Refresh subscription after a short delay to reflect status change
        setTimeout(fetchSubscription, 2000);
      } else {
        showError(response.message || 'Ödeme tekrarı başarısız oldu');
      }
    } catch (error) {
      console.error('Retry payment error:', error);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Ödeme tekrarı sırasında hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const initializePaymentForm = (formContent) => {
    cleanupPaymentForm();

    // Modal içindeki form container'ı bekle ve seç
    setTimeout(() => {
      const formContainer = document.getElementById('iyzipay-checkout-form');
      if (!formContainer) return;

      // Form içeriğini ekle
      formContainer.innerHTML = formContent;

      // Script'leri ayır ve çalıştır
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = formContent;

      const scripts = tempDiv.getElementsByTagName('script');
      Array.from(scripts).forEach(script => {
        const newScript = document.createElement('script');
        if (script.src) {
          newScript.src = script.src;
        }
        if (script.textContent) {
          newScript.textContent = script.textContent;
        }
        document.body.appendChild(newScript);
      });
    }, 100);
  };

  const cleanupPaymentForm = () => {
    const scripts = document.querySelectorAll('script[src*="iyzipay"]');
    scripts.forEach(script => script.remove());

    if (window.iyziInit) window.iyziInit = undefined;
    if (window.iyziUcsInit) window.iyziUcsInit = undefined;
    if (window.iyziSubscriptionInit) window.iyziSubscriptionInit = undefined;
  };

  const proceedWithEditProducts = async () => {
    setActionLoading(true);
    try {
      // Subscription type'ı ayarla - MONTHLY -> biweekly, BIWEEKLY -> monthly olacak şekilde düzelt
      const intervalType = subscription.plan.interval === 'BIWEEKLY' ? 'biweekly' : 'monthly';
      setSubscriptionType(intervalType);

      // Session storage'a kaydet
      const userKey = `selectedSubscriptionType_${subscription.userId || 'guest'}`;
      sessionStorage.setItem(userKey, intervalType);

      // Ürünlerin gerçek fiyatlarını almak için API'den güncel ürün bilgilerini çek
      const productIds = subscription.products.map(product => product.id);
      let updatedProducts = [];

      if (productIds.length > 0) {
        try {
          const productsResponse = await apiRequest(`/products?ids=${productIds.join(',')}`);
          if (productsResponse.success && productsResponse.products) {
            updatedProducts = productsResponse.products;
          }
        } catch (error) {
          console.error('Ürün fiyatları alınırken hata:', error);
          // Hata durumunda mevcut ürünleri kullan
          updatedProducts = subscription.products;
        }
      }

      // Paketteki ürünleri kontrol et
      if (items.length > 0) {
        // Paketteki ürün ID'lerini al
        const existingProductIds = items.map(item => item.id);

        // Abonelik ürünlerinden pakette olmayanları bul
        const newProducts = subscription.products.filter(product => !existingProductIds.includes(product.id));

        if (newProducts.length === 0) {
          // Tüm ürünler zaten pakette varsa
          showInfo('Abonelik ürünleriniz zaten paketinizde bulunmaktadır. Ürünleri düzenlemek için ürünler sayfasına yönlendiriliyorsunuz.');
        } else {
          // Yeni eklenecek ürünler varsa
          showSuccess(`Mevcut paketinize yeni ürün eklenecektir. Ürünleri düzenlemek için ürünler sayfasına yönlendiriliyorsunuz.`);

          // Yeni ürünleri ekle - gerçek fiyatlarla
          newProducts.forEach(product => {
            const updatedProduct = updatedProducts.find(p => p.id === product.id) || product;
            addToPackage({
              ...product,
              price: updatedProduct.price || product.price,
              discountedPrice: updatedProduct.discountedPrice || product.discountedPrice,
              quantity: product.quantity || 1
            });
          });
        }
      } else {
        // Paket boşsa tüm ürünleri ekle - gerçek fiyatlarla
        subscription.products.forEach(product => {
          const updatedProduct = updatedProducts.find(p => p.id === product.id) || product;
          addToPackage({
            ...product,
            price: updatedProduct.price || product.price,
            discountedPrice: updatedProduct.discountedPrice || product.discountedPrice,
            quantity: product.quantity || 1
          });
        });
      }

      // Context'teki düzenleme modunu aktifleştir
      setIsEditing(true);

      // Ürünler sayfasına yönlendir
      navigate('/products');
    } finally {
      setActionLoading(false);
    }
  };



  // Address editing functions
  const handleEditAddresses = () => {
    setShowAddressModal(true);
  };

  const handleAddressInputChange = (e, addressType) => {
    const { name, value } = e.target;
    setAddressFormData(prev => ({
      ...prev,
      [addressType]: {
        ...prev[addressType],
        [name]: value
      }
    }));

    // Auto-fill billing address if same as shipping is checked
    if (addressType === 'shippingAddress' && sameAsBilling) {
      setAddressFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.shippingAddress,
          [name]: value,
          type: prev.billingAddress.type
        }
      }));
    }

    // Clear validation error for this field
    if (validationErrors[`${addressType}.${name}`]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[`${addressType}.${name}`];
        return newErrors;
      });
    }
  };

  const handleSameAsBillingChange = (e) => {
    const checked = e.target.checked;
    setSameAsBilling(checked);

    if (checked) {
      setAddressFormData(prev => ({
        ...prev,
        billingAddress: {
          ...prev.shippingAddress,
          type: prev.billingAddress.type
        }
      }));
    }
  };

  const validateAddressForm = () => {
    const errors = {};

    // Shipping address validation
    if (!addressFormData.shippingAddress.fullName.trim()) {
      errors['shippingAddress.fullName'] = 'Teslimat adresi - Ad Soyad zorunludur';
    }
    if (!addressFormData.shippingAddress.address.trim()) {
      errors['shippingAddress.address'] = 'Teslimat adresi - Adres zorunludur';
    }
    if (!addressFormData.shippingAddress.city.trim()) {
      errors['shippingAddress.city'] = 'Teslimat adresi - Şehir zorunludur';
    }
    if (!addressFormData.shippingAddress.district.trim()) {
      errors['shippingAddress.district'] = 'Teslimat adresi - İlçe zorunludur';
    }
    if (!addressFormData.shippingAddress.phone.trim()) {
      errors['shippingAddress.phone'] = 'Teslimat adresi - Telefon zorunludur';
    }

    // Billing address validation (if not same as shipping)
    if (!sameAsBilling) {
      if (!addressFormData.billingAddress.fullName.trim()) {
        errors['billingAddress.fullName'] = 'Fatura adresi - Ad Soyad zorunludur';
      }
      if (!addressFormData.billingAddress.address.trim()) {
        errors['billingAddress.address'] = 'Fatura adresi - Adres zorunludur';
      }
      if (!addressFormData.billingAddress.city.trim()) {
        errors['billingAddress.city'] = 'Fatura adresi - Şehir zorunludur';
      }
      if (!addressFormData.billingAddress.district.trim()) {
        errors['billingAddress.district'] = 'Fatura adresi - İlçe zorunludur';
      }
      if (!addressFormData.billingAddress.phone.trim()) {
        errors['billingAddress.phone'] = 'Fatura adresi - Telefon zorunludur';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAddressSubmit = async (e) => {
    e.preventDefault();

    if (!validateAddressForm()) {
      return;
    }

    try {
      setActionLoading(true);

      const submitData = {
        shippingAddress: {
          fullName: addressFormData.shippingAddress.fullName,
          address: addressFormData.shippingAddress.address,
          city: addressFormData.shippingAddress.city,
          district: addressFormData.shippingAddress.district,
          country: addressFormData.shippingAddress.country,
          phone: addressFormData.shippingAddress.phone,
          zipCode: addressFormData.shippingAddress.zipCode
        },
        billingAddress: sameAsBilling ? {
          fullName: addressFormData.shippingAddress.fullName,
          address: addressFormData.shippingAddress.address,
          city: addressFormData.shippingAddress.city,
          district: addressFormData.shippingAddress.district,
          country: addressFormData.shippingAddress.country,
          phone: addressFormData.shippingAddress.phone,
          zipCode: addressFormData.shippingAddress.zipCode,
          type: addressFormData.billingAddress.type
        } : {
          fullName: addressFormData.billingAddress.fullName,
          address: addressFormData.billingAddress.address,
          city: addressFormData.billingAddress.city,
          district: addressFormData.billingAddress.district,
          country: addressFormData.billingAddress.country,
          phone: addressFormData.billingAddress.phone,
          zipCode: addressFormData.billingAddress.zipCode,
          type: addressFormData.billingAddress.type
        }
      };

      const response = await apiRequest(`/subscriptions/${subscription.id}/customer-info`, {
        method: 'PUT',
        data: submitData
      });

      if (response.success) {
        showSuccess(response.message || 'Adres bilgileri başarıyla güncellendi');
        setShowAddressModal(false);

        // Update subscription data with new address info from API response or fallback to submitted data
        const updatedSubscription = response.subscription || {};

        setSubscription(prev => ({
          ...prev,
          ...updatedSubscription,
          // If API doesn't return addresses in customerInfo, update them directly
          shippingAddress: updatedSubscription.shippingAddress || {
            ...prev.shippingAddress,
            fullName: submitData.shippingAddress.fullName,
            contactName: submitData.shippingAddress.fullName,
            address: submitData.shippingAddress.address,
            city: submitData.shippingAddress.city,
            district: submitData.shippingAddress.district,
            country: submitData.shippingAddress.country,
            phone: submitData.shippingAddress.phone,
            gsmNumber: submitData.shippingAddress.phone,
            zipCode: submitData.shippingAddress.zipCode
          },
          billingAddress: updatedSubscription.billingAddress || {
            ...prev.billingAddress,
            fullName: submitData.billingAddress.fullName,
            contactName: submitData.billingAddress.fullName,
            address: submitData.billingAddress.address,
            city: submitData.billingAddress.city,
            district: submitData.billingAddress.district,
            country: submitData.billingAddress.country,
            phone: submitData.billingAddress.phone,
            gsmNumber: submitData.billingAddress.phone,
            zipCode: submitData.billingAddress.zipCode,
            type: submitData.billingAddress.type
          },
          // Also update customerInfo structure if needed for backward compatibility
          customerInfo: {
            ...prev.customerInfo,
            ...updatedSubscription.customerInfo,
            shippingAddress: updatedSubscription.shippingAddress || {
              ...prev.customerInfo?.shippingAddress,
              contactName: submitData.shippingAddress.fullName,
              address: submitData.shippingAddress.address,
              city: submitData.shippingAddress.city,
              district: submitData.shippingAddress.district,
              country: submitData.shippingAddress.country,
              gsmNumber: submitData.shippingAddress.phone,
              zipCode: submitData.shippingAddress.zipCode
            },
            billingAddress: updatedSubscription.billingAddress || {
              ...prev.customerInfo?.billingAddress,
              contactName: submitData.billingAddress.fullName,
              address: submitData.billingAddress.address,
              city: submitData.billingAddress.city,
              district: submitData.billingAddress.district,
              country: submitData.billingAddress.country,
              gsmNumber: submitData.billingAddress.phone,
              zipCode: submitData.billingAddress.zipCode
            },
            type: submitData.billingAddress.type
          }
        }));

        setValidationErrors({});
      } else {
        if (response.errors && Array.isArray(response.errors)) {
          const errorMap = {};
          response.errors.forEach(error => {
            errorMap[error.field] = error.message;
          });
          setValidationErrors(errorMap);
        }
        showError(response.message || 'Adres bilgileri güncellenirken bir hata oluştu');
      }
    } catch (error) {
      console.error('Failed to update addresses:', error);
      if (error.apiError) {
        showServerError();
      } else {
        showError(error.message || 'Adres bilgileri güncellenirken bir hata oluştu');
      }
    } finally {
      setActionLoading(false);
    }
  };

  const handleCloseAddressModal = () => {
    setShowAddressModal(false);
    setValidationErrors({});
    // Reset form data to original subscription values - support both API structures
    if (subscription) {
      const customerInfo = subscription.customerInfo;
      const shippingAddr = subscription.shippingAddress || customerInfo?.shippingAddress;
      const billingAddr = subscription.billingAddress || customerInfo?.billingAddress;

      if (shippingAddr || billingAddr || customerInfo) {
        const fullName = customerInfo ? `${customerInfo.name || ''} ${customerInfo.surname || ''}`.trim() : '';

        setAddressFormData({
          shippingAddress: {
            fullName: shippingAddr?.fullName || shippingAddr?.contactName || fullName,
            address: shippingAddr?.address || customerInfo?.registrationAddress || '',
            city: shippingAddr?.city || customerInfo?.city || '',
            district: shippingAddr?.district || customerInfo?.district || '',
            country: shippingAddr?.country || customerInfo?.country || 'Turkey',
            phone: shippingAddr?.phone || shippingAddr?.gsmNumber || customerInfo?.gsmNumber || '',
            zipCode: shippingAddr?.zipCode || customerInfo?.zipCode || ''
          },
          billingAddress: {
            fullName: billingAddr?.fullName || billingAddr?.contactName || fullName,
            address: billingAddr?.address || customerInfo?.registrationAddress || '',
            city: billingAddr?.city || customerInfo?.city || '',
            district: billingAddr?.district || customerInfo?.district || '',
            country: billingAddr?.country || customerInfo?.country || 'Turkey',
            phone: billingAddr?.phone || billingAddr?.gsmNumber || customerInfo?.gsmNumber || '',
            zipCode: billingAddr?.zipCode || customerInfo?.zipCode || '',
            type: billingAddr?.type || customerInfo?.type || 'individual'
          }
        });

        // Reset same as billing checkbox
        if (shippingAddr && billingAddr) {
          const shippingName = shippingAddr.fullName || shippingAddr.contactName;
          const billingName = billingAddr.fullName || billingAddr.contactName;
          const shippingPhone = shippingAddr.phone || shippingAddr.gsmNumber;
          const billingPhone = billingAddr.phone || billingAddr.gsmNumber;

          const isSame = shippingName === billingName &&
            shippingAddr.address === billingAddr.address &&
            shippingAddr.city === billingAddr.city &&
            shippingAddr.district === billingAddr.district &&
            shippingPhone === billingPhone;
          setSameAsBilling(isSame);
        }
      }
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'WAITING':
        return 'warning';
      case 'CANCELLED':
        return 'danger';
      case 'PENDING':
        return 'info';
      case 'FAILED':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'Aktif';
      case 'WAITING':
        return 'Duraklatılmış';
      case 'CANCELLED':
        return 'İptal Edilmiş';
      case 'PENDING':
        return 'Ödeme Bekliyor';
      case 'FAILED':
        return 'Hata Durumu';
      default:
        return status;
    }
  };

  if (notFoundError === 'not_found') {
    return (
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-md-6 text-center">
            <div className="card shadow-sm">
              <div className="card-body p-5">
                <i className="fas fa-box-open fa-3x text-muted mb-4"></i>
                <h3 className="text-olive mb-3">Aboneliğiniz Bulunmamaktadır</h3>
                <p className="text-muted mb-4">
                  Henüz bir aboneliğiniz yok. Hemen yeni bir abonelik oluşturarak Kito'nun lezzetli ürünlerine düzenli olarak ulaşabilirsiniz.
                </p>
                <button
                  className="btn btn-orange btn-lg"
                  onClick={() => navigate('/products')}
                >
                  <i className="fas fa-plus me-2"></i>
                  Abonelik Oluştur
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container py-5">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
          <LoadingSpinner message="Abonelik bilgileri yükleniyor..." size="medium" />
        </div>
      </div>
    );
  }

  if (!subscription) {
    return null;
  }

  return (
    <div className="container py-5 position-relative">
      {/* Loading Overlay */}
      {actionLoading && (
        <div className="loading-overlay">
          <div className="loading-content">
            <div className="loading-spinner">
              <div className="spinner-grow text-orange" role="status">
                <span className="visually-hidden">Yükleniyor...</span>
              </div>
            </div>
            <div className="loading-text">
              <div className="loading-title">İşleminiz Yapılıyor</div>
              <div className="loading-subtitle">Lütfen bekleyiniz...</div>
            </div>
          </div>
        </div>
      )}

      {/* Mevcut içerik */}
      <div className={actionLoading ? 'content-blur' : ''}>
        <div className="row justify-content-center">
          {/* Ana İçerik - Sol Taraf */}
          <div className="col-lg-7">
            {subscription.status === 'PENDING' && (
              <>
                {/* Ödeme formu gösterildi ve son 30 dakikada ise - teyit bekleniyor uyarısı */}
                {subscription.paymentDetails?.paymentFormShownAt &&
                  (new Date() - new Date(subscription.paymentDetails.paymentFormShownAt)) < 30 * 60 * 1000 && (
                    <div className="alert alert-warning mb-3">
                      <div className="d-flex align-items-start">
                        <i className="fas fa-hourglass-half me-3 mt-1"></i>
                        <div>
                          <strong>Ödeme teyidi bekleniyor</strong>
                          <p className="mb-0 mt-1 small">
                            Son ödeme işleminizin sonucu kontrol ediliyor.
                            Eğer ödeme yapmadıysanız bu mesajı dikkate almayın.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                <div className="alert alert-info mb-4">
                  <div className="d-flex align-items-center">
                    <i className="fas fa-info-circle me-2"></i>
                    <div className="flex-grow-1">
                      Aboneliğinizi aktifleştirmek için ödeme yapmanız gerekmektedir.
                    </div>
                    <button
                      className="btn btn-orange btn-sm ms-3"
                      onClick={handlePaymentClick}
                    >
                      <i className="fas fa-credit-card me-2"></i>
                      Ödeme Yap
                    </button>
                  </div>
                </div>
              </>
            )}

            {subscription.status === 'FAILED' && (
              <div className="alert alert-danger mb-4">
                <div className="d-flex align-items-center">
                  <div className="flex-grow-1">
                    <strong>Abonelik Durumu Hatası:</strong> Ödeme alınamadı veya bir sorun oluştu.
                    {subscription.warning && subscription.warning.message && (
                      <span className="d-block mt-1">{subscription.warning.message}</span>
                    )}
                    {subscription.warning && subscription.warning.details && (
                      <div className="mt-2 small">
                        <strong>Detaylar:</strong>
                        <ul className="mb-0 mt-1">
                          <li>Yerel Durum: {subscription.warning.details.localStatus}</li>
                          {subscription.warning.details.detectedAt && (
                            <li>Tespit Tarihi: {new Date(subscription.warning.details.detectedAt).toLocaleDateString('tr-TR')}</li>
                          )}
                        </ul>
                      </div>
                    )}
                  </div>
                  <div className="d-flex flex-column gap-2 ms-3">
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={handleRetryPayment}
                    >
                      <i className="fas fa-redo me-2"></i>
                      Tekrar Dene
                    </button>
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() => window.location.reload()}
                    >
                      <i className="fas fa-sync-alt me-2"></i>
                      Yenile
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="card shadow-sm">
              <div className="card-body">
                <h4 className="text-olive mb-4">
                  <i className="fas fa-file-contract me-2"></i>
                  Abonelik Detayları
                </h4>

                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-info-circle me-2"></i>
                      Genel Durum
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row mb-4">
                      <div className="col-md-6">
                        <p className="text-muted mb-1">
                          Abonelik Durumu
                        </p>
                        <div className="status-indicator-container w-100 h-100">
                          <div className={`status-indicator ${getStatusBadgeClass(subscription.status)} w-100`}>
                            <div className="status-icon">
                              <i className={`fas fa-${subscription.status === 'ACTIVE' ? 'check' :
                                subscription.status === 'PENDING' ? 'clock' :
                                  subscription.status === 'WAITING' ? 'pause' :
                                    subscription.status === 'FAILED' ? 'exclamation-triangle' : 'times'
                                }`}></i>
                            </div>
                            <div className="status-details">
                              <span className="status-text">{getStatusText(subscription.status)}</span>
                              <span className="status-description">
                                {subscription.status === 'ACTIVE' && 'Abonelik aktif'}
                                {subscription.status === 'PENDING' && 'Ödeme bekleniyor'}
                                {subscription.status === 'WAITING' && 'Geçici durduruldu'}
                                {subscription.status === 'CANCELLED' && 'Abonelik iptal'}
                                {subscription.status === 'FAILED' && 'Hata tespit edildi'}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-6">
                        <p className="text-muted mb-1">
                          <i className="fas fa-calendar me-2"></i>
                          Başlangıç Tarihi
                        </p>
                        <p className="h6">{new Date(subscription.startDate).toLocaleDateString('tr-TR')}</p>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-md-6">
                        <p className="text-muted mb-1">
                          <i className="fas fa-clock me-2"></i>
                          Sonraki Ödeme
                        </p>
                        <p className="h6">{subscription.nextPaymentDate ? new Date(subscription.nextPaymentDate).toLocaleDateString('tr-TR') : '-'}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="text-muted mb-1">
                          <i className="fas fa-history me-2"></i>
                          Son Ödeme
                        </p>
                        <p className="h6">
                          {subscription.lastPaymentDate ?
                            new Date(subscription.lastPaymentDate).toLocaleDateString('tr-TR') :
                            '-'
                          }
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card mb-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-cube me-2"></i>
                      Plan Bilgileri
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="row">
                      <div className="col-md-6">
                        <p className="text-muted mb-1">Plan Adı</p>
                        <p className="h6">{subscription.plan.name}</p>
                      </div>
                      <div className="col-md-6">
                        <p className="text-muted mb-1">Teslimat Sıklığı</p>
                        <p className="h6">
                          {subscription.plan.interval === 'MONTHLY' ?
                            `Aylık` :
                            '2 Haftalık'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-box me-2"></i>
                      Ürünler
                    </h6>
                  </div>
                  <div className="list-group list-group-flush">
                    {subscription.products.map((product) => (
                      <div key={product.id} className="list-group-item">
                        <div className="d-flex align-items-center">
                          <img
                            src={product.mainImage || placeholderImage}
                            alt={product.name}
                            className="rounded me-3"
                            style={{ width: '60px', height: '60px', objectFit: 'cover' }}
                          />
                          <div className="flex-grow-1">
                            <h6 className="product-name mb-1">{product.name}</h6>
                            <p className="text-muted small mb-0">
                              <span className="badge bg-light text-dark me-2">
                                <i className="fas fa-box me-1"></i>
                                {product.quantity} adet
                              </span>
                              <span className="badge bg-light text-dark">
                                <i className="fas fa-tag me-1"></i>
                                {formatPrice(product.price)}
                              </span>
                            </p>
                          </div>
                          <div className="text-end ms-3">
                            <div className="text-orange fw-bold price-display">
                              {formatPrice(product.price * product.quantity)}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Address Information Cards */}
                {(subscription.shippingAddress || subscription.billingAddress || subscription.customerInfo) && (
                  <div className="row mt-4">
                    {/* Shipping Address - Support both direct and customerInfo structures */}
                    {(subscription.shippingAddress || subscription.customerInfo?.shippingAddress) && (
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="fas fa-truck me-2"></i>
                              Teslimat Adresi
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="address-info">
                              {(() => {
                                const shippingAddr = subscription.shippingAddress || subscription.customerInfo?.shippingAddress;
                                const customerInfo = subscription.customerInfo;

                                return (
                                  <>
                                    <p className="mb-1">
                                      <strong>
                                        {shippingAddr?.fullName || shippingAddr?.contactName ||
                                          (customerInfo ? `${customerInfo.name || ''} ${customerInfo.surname || ''}`.trim() : '')}
                                      </strong>
                                    </p>
                                    <p className="mb-1">
                                      {shippingAddr?.openAddress || shippingAddr?.address || customerInfo?.registrationAddress}
                                    </p>
                                    <p className="mb-1">
                                      {shippingAddr?.district || customerInfo?.district}, {shippingAddr?.city || customerInfo?.city}
                                    </p>
                                    {(shippingAddr?.zipCode || customerInfo?.zipCode) && (
                                      <p className="mb-1">{shippingAddr?.zipCode || customerInfo?.zipCode}</p>
                                    )}
                                    <p className="mb-0 text-muted">
                                      <i className="fas fa-phone me-1"></i>
                                      {shippingAddr?.phone || shippingAddr?.gsmNumber || customerInfo?.gsmNumber}
                                    </p>
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Billing Address - Support both direct and customerInfo structures */}
                    {(subscription.billingAddress || subscription.customerInfo?.billingAddress) && (
                      <div className="col-md-6">
                        <div className="card">
                          <div className="card-header bg-light">
                            <h6 className="mb-0">
                              <i className="fas fa-receipt me-2"></i>
                              Fatura Adresi
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="address-info">
                              {(() => {
                                const billingAddr = subscription.billingAddress || subscription.customerInfo?.billingAddress;
                                const customerInfo = subscription.customerInfo;

                                return (
                                  <>
                                    <p className="mb-1">
                                      <strong>
                                        {billingAddr?.fullName || billingAddr?.contactName ||
                                          (customerInfo ? `${customerInfo.name || ''} ${customerInfo.surname || ''}`.trim() : '')}
                                      </strong>
                                    </p>
                                    <p className="mb-1">
                                      {billingAddr?.openAddress || billingAddr?.address || customerInfo?.registrationAddress}
                                    </p>
                                    <p className="mb-1">
                                      {billingAddr?.district || customerInfo?.district}, {billingAddr?.city || customerInfo?.city}
                                    </p>
                                    {(billingAddr?.zipCode || customerInfo?.zipCode) && (
                                      <p className="mb-1">{billingAddr?.zipCode || customerInfo?.zipCode}</p>
                                    )}
                                    <p className="mb-1 text-muted">
                                      <i className="fas fa-phone me-1"></i>
                                      {billingAddr?.phone || billingAddr?.gsmNumber || customerInfo?.gsmNumber}
                                    </p>
                                    {billingAddr?.type && (
                                      <p className="mb-0">
                                        <span className={`badge ${(billingAddr?.type || customerInfo?.type) === 'individual' ? 'bg-success' : 'bg-info'}`}>
                                          {(billingAddr?.type || customerInfo?.type) === 'individual' ? 'Bireysel' : 'Kurumsal'}
                                        </span>
                                      </p>
                                    )}
                                  </>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="card mt-4">
                  <div className="card-header bg-light">
                    <h6 className="mb-0">
                      <i className="fas fa-receipt me-2"></i>
                      Ödeme Özeti
                    </h6>
                  </div>
                  <div className="card-body">
                    <div className="d-flex justify-content-between border-top pt-2">
                      <strong>Toplam</strong>
                      <strong className="text-orange">
                        {formatPrice(subscription.paymentSummary?.total)}
                      </strong>
                    </div>
                    {subscription.appliedDiscountCode && (
                      <div className="d-flex justify-content-between mt-2">
                        <span className="text-muted small">İndirim Kodu:</span>
                        <span className="text-success small">
                          <i className="fas fa-tag me-1"></i>
                          {subscription.appliedDiscountCode}
                        </span>
                      </div>
                    )}
                    <div className="text-muted small mt-2">
                      <i className="fas fa-info-circle me-1"></i>
                      Sonraki Ödeme Tutarı: {formatPrice(subscription.paymentSummary?.nextPaymentAmount)}
                    </div>
                  </div>
                </div>


                {subscription.status === 'PENDING' && (
                  <div className="d-grid mt-4">
                    <button
                      className="btn btn-orange btn-lg"
                      onClick={handlePaymentClick}
                    >
                      <i className="fas fa-credit-card me-2"></i>
                      Ödemeye Devam Et
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Görsel Öğeler - Sağ Taraf */}
          <div className="col-lg-3">
            {/* Hızlı İşlemler ve Durum Kartı */}
            <div className="card shadow-sm mb-3">
              <div className="card-body">

                <h5 className="card-title d-flex align-items-center mb-3">
                  <i className="fas fa-bolt text-warning me-2"></i>
                  Hızlı İşlemler
                </h5>

                {subscription?.status === 'ACTIVE' && (
                  <div className="quick-actions-grid">
                    <button
                      className="quick-action-btn"
                      onClick={handleUpdateCard}
                    >
                      <div className="icon-wrapper warning">
                        <i className="fas fa-credit-card"></i>
                      </div>
                      <span className="action-text">Kart Güncelle</span>
                    </button>
                    <button
                      className="quick-action-btn edit"
                      onClick={handleEditProducts}
                    >
                      <div className="icon-wrapper info">
                        <i className="fas fa-edit"></i>
                      </div>
                      <span className="action-text">Ürünleri Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn address"
                      onClick={handleEditAddresses}
                    >
                      <div className="icon-wrapper success">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                      <span className="action-text">Adres Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn history"
                      onClick={() => setShowHistoryModal(true)}
                    >
                      <div className="icon-wrapper secondary">
                        <i className="fas fa-history"></i>
                      </div>
                      <span className="action-text">Geçmiş Aboneliklerim</span>
                    </button>

                  </div>
                )}

                {subscription?.status === 'WAITING' && (
                  <div className="quick-actions-grid">
                    <button
                      className="quick-action-btn"
                      onClick={handleUpdateCard}
                    >
                      <div className="icon-wrapper warning">
                        <i className="fas fa-credit-card"></i>
                      </div>
                      <span className="action-text">Kart Güncelle</span>
                    </button>
                    <button
                      className="quick-action-btn edit"
                      onClick={handleEditProducts}
                    >
                      <div className="icon-wrapper info">
                        <i className="fas fa-edit"></i>
                      </div>
                      <span className="action-text">Ürünleri Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn address"
                      onClick={handleEditAddresses}
                    >
                      <div className="icon-wrapper olive">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                      <span className="action-text">Adres Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn history"
                      onClick={() => setShowHistoryModal(true)}
                    >
                      <div className="icon-wrapper secondary">
                        <i className="fas fa-history"></i>
                      </div>
                      <span className="action-text">Geçmiş Aboneliklerim</span>
                    </button>

                  </div>
                )}

                {subscription?.status === 'PENDING' && (
                  <div className="quick-actions-grid">
                    <button
                      className="quick-action-btn payment"
                      onClick={handlePaymentClick}
                    >
                      <div className="icon-wrapper orange">
                        <i className="fas fa-credit-card"></i>
                      </div>
                      <span className="action-text">Ödemeyi Tamamla</span>
                    </button>

                    <button
                      className="quick-action-btn edit"
                      onClick={handleEditProducts}
                    >
                      <div className="icon-wrapper info">
                        <i className="fas fa-edit"></i>
                      </div>
                      <span className="action-text">Ürünleri Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn address"
                      onClick={handleEditAddresses}
                    >
                      <div className="icon-wrapper olive">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                      <span className="action-text">Adres Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn history"
                      onClick={() => setShowHistoryModal(true)}
                    >
                      <div className="icon-wrapper secondary">
                        <i className="fas fa-history"></i>
                      </div>
                      <span className="action-text">Geçmiş Aboneliklerim</span>
                    </button>

                  </div>
                )}

                {subscription?.status === 'CANCELLED' && (
                  <div className="quick-actions-grid">
                    <button
                      className="quick-action-btn edit"
                      onClick={handleEditProducts}
                    >
                      <div className="icon-wrapper info">
                        <i className="fas fa-edit"></i>
                      </div>
                      <span className="action-text">Ürünleri Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn address"
                      onClick={handleEditAddresses}
                    >
                      <div className="icon-wrapper olive">
                        <i className="fas fa-map-marker-alt"></i>
                      </div>
                      <span className="action-text">Adres Düzenle</span>
                    </button>

                    <button
                      className="quick-action-btn history"
                      onClick={() => setShowHistoryModal(true)}
                    >
                      <div className="icon-wrapper secondary">
                        <i className="fas fa-history"></i>
                      </div>
                      <span className="action-text">Geçmiş Aboneliklerim</span>
                    </button>

                    <div className="alert custom-alert mb-0">
                      <div className="alert-icon">
                        <i className="fas fa-info-circle"></i>
                      </div>
                      <div className="alert-content">
                        Yeni bir abonelik başlatmak için ana sayfadan ürün seçebilirsiniz.
                      </div>
                    </div>
                  </div>
                )}

                {subscription?.status === 'FAILED' && (
                  <div className="quick-actions-grid">
                    <button
                      className="quick-action-btn refresh"
                      onClick={() => window.location.reload()}
                    >
                      <div className="icon-wrapper warning">
                        <i className="fas fa-sync-alt"></i>
                      </div>
                      <span className="action-text">Durumu Yenile</span>
                    </button>

                    <button
                      className="quick-action-btn support"
                      onClick={() => window.open('mailto:info@kito.pet?subject=Abonelik Durumu Hatası', '_blank')}
                    >
                      <div className="icon-wrapper info">
                        <i className="fas fa-headset"></i>
                      </div>
                      <span className="action-text">Destek Al</span>
                    </button>



                    <button
                      className="quick-action-btn"
                      onClick={handleUpdateCard}
                    >
                      <div className="icon-wrapper warning">
                        <i className="fas fa-credit-card"></i>
                      </div>
                      <span className="action-text">Kart Güncelle</span>
                    </button>

                    <button
                      className="quick-action-btn history"
                      onClick={() => setShowHistoryModal(true)}
                    >
                      <div className="icon-wrapper secondary">
                        <i className="fas fa-history"></i>
                      </div>
                      <span className="action-text">Geçmiş Aboneliklerim</span>
                    </button>


                    <div className="alert custom-alert mb-0">
                      <div className="alert-icon">
                        <i className="fas fa-exclamation-triangle"></i>
                      </div>
                      <div className="alert-content">
                        Abonelik durumunuzda hata tespit edildi. Lütfen destek ekibi ile iletişime geçin.
                      </div>
                    </div>
                  </div>
                )}

                {/* Cancel Link at the bottom of Quick Actions for relevant statuses */}
                {['ACTIVE', 'WAITING', 'PENDING', 'FAILED'].includes(subscription?.status) && (
                  <div className="mt-3 text-center border-top pt-3">
                    <button
                      className="btn btn-link text-muted text-decoration-none btn-sm"
                      onClick={handleCancelSubscription}
                    >
                      Aboneliği İptal Et
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Teslimat Zamanı Kartı */}
            <div className="card shadow-sm mb-3">
              <div className="card-body p-3">
                <div className="delivery-timeline">
                  <div className="timeline-header d-flex align-items-center mb-3">
                    <div className="delivery-icon-wrapper me-3">
                      <i className="fas fa-truck text-olive"></i>
                    </div>
                    <div>
                      <h6 className="mb-1 small">Sonraki Teslimat</h6>
                      <p className="mb-0 text-olive fw-bold">
                        {subscription.nextPaymentDate ? new Date(subscription.nextPaymentDate).toLocaleDateString('tr-TR') : 'Bilgi Yok'}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3">
                    <button
                      className="btn btn-olive w-100"
                      onClick={() => navigate('/orders')}
                    >
                      <i className="fas fa-box me-2"></i>
                      Siparişlerim
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Ödeme Özeti Kartı */}
            <div className="card shadow-sm mb-3">
              <div className="card-body p-3">
                <div className="text-center mb-3">
                  <h4 className="text-orange mb-1">
                    {formatPrice(subscription.paymentSummary?.total)}
                  </h4>
                  <p className="text-muted small mb-0">Aylık Tutar</p>
                </div>
                <hr className="my-2" />
                <div className="d-flex justify-content-between mb-1">
                  <span className="text-muted small">Ürün Sayısı</span>
                  <span className="fw-bold small">{subscription.products.length} Ürün</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted small">Teslimat Sıklığı</span>
                  <span className="fw-bold small">
                    {subscription.plan.interval === 'MONTHLY' ?
                      `Aylık` :
                      '2 Haftalık'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Update Modal */}
      {showPaymentModal && (
        <div className="payment-modal">
          <div className="payment-modal-content">
            <button
              className="payment-modal-close"
              onClick={() => {
                setShowPaymentModal(false);
                cleanupPaymentForm();
              }}
            >
              &times;
            </button>
            <h5 className="mb-4">Kart Bilgilerini Güncelle</h5>
            <div id="iyzipay-checkout-form" className="responsive"></div>
          </div>
        </div>
      )}

      {/* Address Edit Modal */}
      {showAddressModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Adres Düzenle</h5>
              <button type="button" className="btn-close" onClick={handleCloseAddressModal}>×</button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleAddressSubmit}>
                <div className="row">
                  <div className="col-md-6">
                    <h6>Teslimat Adresi</h6>
                    <div className="mb-3">
                      <label htmlFor="shippingFullName" className="form-label">Ad Soyad</label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingFullName"
                        name="fullName"
                        value={addressFormData.shippingAddress.fullName}
                        onChange={(e) => handleAddressInputChange(e, 'shippingAddress')}
                        required
                      />
                      {validationErrors['shippingAddress.fullName'] && (
                        <div className="text-danger small">{validationErrors['shippingAddress.fullName']}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="shippingAddress" className="form-label">Adres</label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingAddress"
                        name="address"
                        value={addressFormData.shippingAddress.address}
                        onChange={(e) => handleAddressInputChange(e, 'shippingAddress')}
                        required
                      />
                      {validationErrors['shippingAddress.address'] && (
                        <div className="text-danger small">{validationErrors['shippingAddress.address']}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="shippingCity" className="form-label">Şehir</label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingCity"
                        name="city"
                        value={addressFormData.shippingAddress.city}
                        onChange={(e) => handleAddressInputChange(e, 'shippingAddress')}
                        required
                      />
                      {validationErrors['shippingAddress.city'] && (
                        <div className="text-danger small">{validationErrors['shippingAddress.city']}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="shippingDistrict" className="form-label">İlçe</label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingDistrict"
                        name="district"
                        value={addressFormData.shippingAddress.district}
                        onChange={(e) => handleAddressInputChange(e, 'shippingAddress')}
                        required
                      />
                      {validationErrors['shippingAddress.district'] && (
                        <div className="text-danger small">{validationErrors['shippingAddress.district']}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="shippingPhone" className="form-label">Telefon</label>
                      <input
                        type="tel"
                        className="form-control"
                        id="shippingPhone"
                        name="phone"
                        value={addressFormData.shippingAddress.phone}
                        onChange={(e) => handleAddressInputChange(e, 'shippingAddress')}
                        required
                      />
                      {validationErrors['shippingAddress.phone'] && (
                        <div className="text-danger small">{validationErrors['shippingAddress.phone']}</div>
                      )}
                    </div>
                    <div className="mb-3">
                      <label htmlFor="shippingZipCode" className="form-label">Posta Kodu</label>
                      <input
                        type="text"
                        className="form-control"
                        id="shippingZipCode"
                        name="zipCode"
                        value={addressFormData.shippingAddress.zipCode}
                        onChange={(e) => handleAddressInputChange(e, 'shippingAddress')}
                      />
                    </div>
                  </div>
                  <div className="col-md-6">
                    <h6>Fatura Adresi</h6>
                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="sameAsShipping"
                        checked={sameAsBilling}
                        onChange={handleSameAsBillingChange}
                      />
                      <label htmlFor="sameAsShipping" className="form-check-label">
                        Teslimat adresi ile aynı
                      </label>
                    </div>
                    {!sameAsBilling && (
                      <>
                        <div className="mb-3">
                          <label htmlFor="billingFullName" className="form-label">Ad Soyad</label>
                          <input
                            type="text"
                            className="form-control"
                            id="billingFullName"
                            name="fullName"
                            value={addressFormData.billingAddress.fullName}
                            onChange={(e) => handleAddressInputChange(e, 'billingAddress')}
                            required
                          />
                          {validationErrors['billingAddress.fullName'] && (
                            <div className="text-danger small">{validationErrors['billingAddress.fullName']}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label htmlFor="billingAddress" className="form-label">Adres</label>
                          <input
                            type="text"
                            className="form-control"
                            id="billingAddress"
                            name="address"
                            value={addressFormData.billingAddress.address}
                            onChange={(e) => handleAddressInputChange(e, 'billingAddress')}
                            required
                          />
                          {validationErrors['billingAddress.address'] && (
                            <div className="text-danger small">{validationErrors['billingAddress.address']}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label htmlFor="billingCity" className="form-label">Şehir</label>
                          <input
                            type="text"
                            className="form-control"
                            id="billingCity"
                            name="city"
                            value={addressFormData.billingAddress.city}
                            onChange={(e) => handleAddressInputChange(e, 'billingAddress')}
                            required
                          />
                          {validationErrors['billingAddress.city'] && (
                            <div className="text-danger small">{validationErrors['billingAddress.city']}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label htmlFor="billingDistrict" className="form-label">İlçe</label>
                          <input
                            type="text"
                            className="form-control"
                            id="billingDistrict"
                            name="district"
                            value={addressFormData.billingAddress.district}
                            onChange={(e) => handleAddressInputChange(e, 'billingAddress')}
                            required
                          />
                          {validationErrors['billingAddress.district'] && (
                            <div className="text-danger small">{validationErrors['billingAddress.district']}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label htmlFor="billingPhone" className="form-label">Telefon</label>
                          <input
                            type="tel"
                            className="form-control"
                            id="billingPhone"
                            name="phone"
                            value={addressFormData.billingAddress.phone}
                            onChange={(e) => handleAddressInputChange(e, 'billingAddress')}
                            required
                          />
                          {validationErrors['billingAddress.phone'] && (
                            <div className="text-danger small">{validationErrors['billingAddress.phone']}</div>
                          )}
                        </div>
                        <div className="mb-3">
                          <label htmlFor="billingZipCode" className="form-label">Posta Kodu</label>
                          <input
                            type="text"
                            className="form-control"
                            id="billingZipCode"
                            name="zipCode"
                            value={addressFormData.billingAddress.zipCode}
                            onChange={(e) => handleAddressInputChange(e, 'billingAddress')}
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={handleCloseAddressModal}>İptal</button>
                  <button
                    type="submit"
                    className="btn btn-orange"
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        Kaydediliyor...
                      </>
                    ) : 'Kaydet'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Abonelik İptali</h5>
              <button type="button" className="btn-close" onClick={() => setShowCancelModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <p>Aboneliğinizi iptal etmek istediğinizden emin misiniz?</p>
                <p className="text-muted small">Bu işlem geri alınamaz.</p>
              </div>

              <div className="mb-3">
                <label htmlFor="cancelReason" className="form-label">İptal Sebebi <span className="text-danger">*</span></label>
                <textarea
                  className="form-control"
                  id="cancelReason"
                  rows="4"
                  placeholder="Lütfen aboneliğinizi iptal etme sebebinizi belirtiniz..."
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  maxLength="500"
                ></textarea>
                <div className="form-text">{cancelReason.length}/500 karakter</div>
              </div>

              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="cancelOrders"
                    checked={cancelOrders}
                    onChange={(e) => setCancelOrders(e.target.checked)}
                  />
                  <label htmlFor="cancelOrders" className="form-check-label">
                    <strong>Bekleyen siparişleri iptal et</strong>
                    <div className="text-muted small">
                      Henüz kargoya verilmemiş siparişleriniz için iptal talebi oluşturulacaktır (İptal/iade garantisi vermez, ekibimiz inceleyecektir).
                    </div>
                  </label>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowCancelModal(false);
                  setCancelReason('');
                  setCancelOrders(false);
                }}
                disabled={actionLoading}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={handleConfirmCancel}
                disabled={actionLoading || !cancelReason.trim()}
              >
                {actionLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    İptal Ediliyor...
                  </>
                ) : 'Aboneliği İptal Et'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Products Confirmation Modal */}
      {showEditConfirmModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Ürün Düzenleme Onayı</h5>
              <button type="button" className="btn-close" onClick={() => setShowEditConfirmModal(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="mb-3">
                <div className="alert alert-info">
                  <i className="fas fa-info-circle me-2"></i>
                  <strong>Önemli Bilgi:</strong>
                </div>
                <p>Aboneliğinizi güncellediğinizde bir sonraki ödeme döneminde siparişiniz oluşacaktır.</p>
                <p className="text-muted small">Daha erken almak isterseniz iptal edip yeni abonelik oluşturabilirsiniz.</p>
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowEditConfirmModal(false)}
              >
                Vazgeç
              </button>
              <button
                type="button"
                className="btn btn-orange"
                onClick={() => {
                  setShowEditConfirmModal(false);
                  proceedWithEditProducts();
                }}
              >
                Devam Et
              </button>
            </div>
          </div>
        </div>
      )}

      {/* History Modal */}
      {showHistoryModal && (
        <div className="modal-overlay" onClick={() => setShowHistoryModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h5 className="modal-title fw-bold text-dark">Geçmiş Aboneliklerim</h5>
              <button type="button" className="btn-close" onClick={() => setShowHistoryModal(false)}>×</button>
            </div>
            <div className="modal-body p-0">
              {pastSubscriptions.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0 align-middle">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0 ps-4 py-3 text-muted small fw-bold text-uppercase">Paket</th>
                        <th className="border-0 py-3 text-muted small fw-bold text-uppercase">Başlangıç</th>
                        <th className="border-0 py-3 text-muted small fw-bold text-uppercase">Bitiş</th>
                        <th className="border-0 py-3 text-muted small fw-bold text-uppercase text-center">Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {pastSubscriptions.map((sub) => (
                        <tr key={sub.id}>
                          <td className="ps-4">
                            <div className="fw-medium text-dark">{sub.planName || 'Bilinmeyen Paket'}</div>
                            <div className="small text-muted">
                              {sub.products?.length} Ürün • {sub.price ? new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(sub.price) : '-'}
                            </div>
                          </td>
                          <td className="text-muted small">
                            <div className="d-flex align-items-center">
                              <i className="far fa-calendar-alt me-2"></i>
                              {new Date(sub.startDate).toLocaleDateString('tr-TR')}
                            </div>
                          </td>
                          <td className="text-muted small">
                            {sub.endDate ? (
                              <div className="d-flex align-items-center">
                                <i className="far fa-calendar-times me-2"></i>
                                {new Date(sub.endDate).toLocaleDateString('tr-TR')}
                              </div>
                            ) : '-'}
                          </td>
                          <td className="text-center">
                            <span
                              className="badge rounded-pill px-3 py-2"
                              style={
                                sub.status === 'CANCELLED' ? { backgroundColor: '#6c757d', color: '#fff' } :
                                sub.status === 'EXPIRED' ? { backgroundColor: '#ffc107', color: '#212529' } :
                                sub.status === 'FAILED' ? { backgroundColor: '#dc3545', color: '#fff' } :
                                { backgroundColor: '#f8f9fa', color: '#212529', border: '1px solid #dee2e6' }
                              }
                            >
                              {sub.status === 'CANCELLED' ? 'İptal Edildi' :
                                sub.status === 'EXPIRED' ? 'Süresi Doldu' :
                                  sub.status === 'FAILED' ? 'Ödeme Hatası' : sub.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-5 text-muted">
                  <i className="fas fa-history fa-3x mb-3 text-orange opacity-50"></i>
                  <p className="mb-0 fw-medium text-dark opacity-75">Geçmiş abonelik kaydınız bulunmamaktadır.</p>
                </div>
              )}
            </div>
            <div className="modal-footer border-0">
              <button className="btn btn-orange px-4 rounded-pill" onClick={() => setShowHistoryModal(false)}>Kapat</button>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .status-circle {
            width: 60px;
            height: 60px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .price-circle {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .delivery-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background-color: var(--kito-olive);
            background-opacity: 0.1;
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
          }
          .progress {
            border-radius: 10px;
            background-color: #f0f0f0;
          }
          .progress-bar {
            border-radius: 10px;
          }
          .card {
            border: none;
          }
          .btn-sm {
            padding: 0.5rem 1rem;
          }
          .delivery-icon-wrapper {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background-color: rgba(var(--kito-olive-rgb), 0.1);
            display: flex;
            align-items: center;
            justify-content: center;
          }
          .delivery-icon-wrapper i {
            color: var(--kito-olive);
          }
          .timeline-progress {
            padding: 10px 0;
          }
          .timeline-steps {
            display: flex;
            justify-content: space-between;
            position: relative;
            margin-top: 15px;
          }
          .timeline-steps:before {
            content: '';
            position: absolute;
            top: 20px;
            left: 15%;
            right: 15%;
            height: 2px;
            background: #e9ecef;
            z-index: 1;
          }
          .timeline-step {
            position: relative;
            z-index: 2;
            flex: 1;
            text-align: center;
          }
          .step-icon {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            background: #fff;
            border: 2px solid #e9ecef;
            display: flex;
            align-items: center;
            justify-content: center;
            margin: 0 auto 8px;
            transition: all 0.3s ease;
          }
          .step-icon i {
            color: #adb5bd;
            font-size: 1rem;
          }
          .step-label {
            font-size: 0.75rem;
            color: #6c757d;
            margin-top: 4px;
          }
          .timeline-step.active .step-icon {
            border-color: var(--kito-olive);
            background-color: rgba(var(--kito-olive-rgb), 0.1);
          }
          .timeline-step.active .step-icon i {
            color: var(--kito-olive);
          }
          .timeline-step.active .step-label {
            color: var(--kito-olive);
            font-weight: 600;
          }
          .timeline-step.completed .step-icon {
            background-color: var(--kito-olive);
            border-color: var(--kito-olive);
          }
          .timeline-step.completed .step-icon i {
            color: #fff;
          }
          .status-indicator {
            display: flex;
            align-items: center;
            padding: 12px 16px;
            border-radius: 8px;
            background-color: #f8f9fa;
            border-left: 4px solid;
            transition: all 0.3s ease;
          }

          .status-indicator.success {
            background-color: rgba(25, 135, 84, 0.1);
            border-left-color: #198754;
          }

          .status-indicator.warning {
            background-color: rgba(255, 193, 7, 0.1);
            border-left-color: #ffc107;
          }

          .status-indicator.danger {
            background-color: rgba(220, 53, 69, 0.1);
            border-left-color: #dc3545;
          }

          .status-indicator.info {
            background-color: rgba(13, 202, 240, 0.1);
            border-left-color: #0dcaf0;
          }

          .status-icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
          }

          .status-indicator.success .status-icon {
            background-color: #198754;
            color: white;
          }

          .status-indicator.warning .status-icon {
            background-color: #ffc107;
            color: #000;
          }

          .status-indicator.danger .status-icon {
            background-color: #dc3545;
            color: white;
          }

          .status-indicator.info .status-icon {
            background-color: #0dcaf0;
            color: white;
          }

          .status-details {
            display: flex;
            flex-direction: column;
          }

          .status-text {
            font-weight: 600;
            font-size: 0.9rem;
            margin-bottom: 2px;
          }

          .status-description {
            font-size: 0.75rem;
            color: #6c757d;
          }

          .product-name {
            font-size: 0.9rem;
            color: #2d3436;
            margin-bottom: 4px;
          }

          .price-display {
            display: flex;
            align-items: center;
            gap: 4px;
          }

          .price-display .amount {
            font-size: 1rem;
          }

          .price-display .currency {
            font-size: 0.9rem;
          }

          /* Hızlı İşlemler Stilleri */
          .quick-actions-grid {
            display: grid;
            grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
            gap: 1rem;
          }

          .quick-action-btn {
            display: flex;
            align-items: center;
            padding: 1rem;
            border: none;
            border-radius: 8px;
            background-color: #fff;
            transition: all 0.3s ease;
            box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            cursor: pointer;
            width: 100%;
          }

          .quick-action-btn:hover {
            transform: translateY(-2px);
            box-shadow: 0 4px 8px rgba(0,0,0,0.1);
          }

          .icon-wrapper {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            transition: all 0.3s ease;
          }

          .icon-wrapper i {
            font-size: 1.2rem;
            color: white;
          }

          .action-text {
            font-size: 0.9rem;
            font-weight: 500;
            color: #2d3436;
          }

          .icon-wrapper.warning {
            background-color: #ffc107;
          }

          .icon-wrapper.info {
            background-color: #0dcaf0;
          }

          .icon-wrapper.danger {
            background-color: #dc3545;
          }

          .icon-wrapper.success {
            background-color: #198754;
          }

          .icon-wrapper.olive {
            background-color: var(--kito-olive);
          }

          .icon-wrapper.secondary {
            background-color: #6c757d;
          }

          .icon-wrapper.orange {
            background-color: var(--kito-orange);
          }

          .custom-alert {
            display: flex;
            align-items: center;
            background-color: rgba(13, 202, 240, 0.1);
            border: none;
            border-radius: 8px;
            padding: 1rem;
          }

          .alert-icon {
            width: 40px;
            height: 40px;
            border-radius: 10px;
            background-color: #0dcaf0;
            display: flex;
            align-items: center;
            justify-content: center;
            margin-right: 12px;
            flex-shrink: 0;
          }

          .alert-icon i {
            color: white;
            font-size: 1.2rem;
            display: block;
          }

          .alert-content {
            color: #2d3436;
            font-size: 0.9rem;
            flex: 1;
            min-width: 0;
          }

          .loading-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(255, 255, 255, 0.9);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 9999;
          }

          .loading-content {
            text-align: center;
            padding: 2rem;
            border-radius: 1rem;
            background: white;
            box-shadow: 0 0 40px rgba(0, 0, 0, 0.1);
            min-width: 280px;
            max-width: 90%;
            animation: fadeIn 0.3s ease;
          }

          .loading-spinner {
            margin-bottom: 1.5rem;
          }

          .loading-text {
            color: #2d3436;
          }

          .loading-title {
            font-size: 1.25rem;
            font-weight: 600;
            margin-bottom: 0.5rem;
            color: var(--kito-orange);
          }

          .loading-subtitle {
            font-size: 0.9rem;
            color: #6c757d;
          }

          .spinner-grow {
            width: 3rem;
            height: 3rem;
          }

          @keyframes fadeIn {
            from {
              opacity: 0;
              transform: translateY(-20px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }

          /* Modal Styles */
          .modal-overlay {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background-color: rgba(0, 0, 0, 0.5);
            display: flex;
            justify-content: center;
            align-items: center;
            z-index: 10000;
          }

          .modal-content {
            background-color: white;
            border-radius: 8px;
            box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
            max-width: 800px;
            width: 90%;
            max-height: 90vh;
            overflow-y: auto;
          }

          .modal-header {
            padding: 1rem 1.5rem;
            border-bottom: 1px solid #dee2e6;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .modal-title {
            margin: 0;
            font-size: 1.25rem;
            color: var(--kito-olive);
          }

          .btn-close {
            border: none;
            background: none;
            font-size: 1.5rem;
            cursor: pointer;
            color: #6c757d;
            padding: 0;
            margin-left: auto;
          }

          .btn-close:hover {
            color: #000;
          }

          .modal-body {
            padding: 1.5rem;
          }

          .modal-footer {
            padding: 1rem 1.5rem;
            border-top: 1px solid #dee2e6;
            display: flex;
            justify-content: flex-end;
            gap: 0.5rem;
          }

          .address-info p {
            margin-bottom: 0.25rem;
            font-size: 0.9rem;
          }

          .address-info .text-muted {
            font-size: 0.85rem;
          }

          @media (max-width: 768px) {
            .modal-content {
              width: 95%;
              margin: 1rem;
            }
            
            .quick-actions-grid {
              grid-template-columns: 1fr 1fr;
            }
          }

          @media (max-width: 576px) {
            .loading-content {
              padding: 1.5rem;
              min-width: 250px;
            }

            .loading-title {
              font-size: 1.1rem;
            }

            .loading-subtitle {
              font-size: 0.85rem;
            }

            .spinner-grow {
              width: 2.5rem;
              height: 2.5rem;
            }

            .quick-actions-grid {
              grid-template-columns: 1fr;
            }

            .custom-alert {
              padding: 0.75rem;
            }

            .alert-icon {
              width: 32px;
              height: 32px;
              margin-right: 8px;
            }

            .alert-icon i {
              font-size: 1rem;
            }

            .alert-content {
              font-size: 0.85rem;
            }
          }

          .btn-olive {
            background-color: var(--kito-olive);
            border-color: var(--kito-olive);
            color: white;
          }

          .btn-olive:hover {
            background-color: #3a4a2e;
            border-color: #3a4a2e;
            color: white;
          }
        `}
      </style>
    </div>
  );
}

export default UserSubscription; 